import { useEffect, useRef, useState } from 'react';
import type { ArModelBlobs } from './arModel';
import type { MapStyleKey } from './MapboxMap';

// print size label (e.g. "55cm") -> real-world side length in meters
const SIZE_METERS: Record<string, number> = {
  '55cm': 0.55,
  '65cm': 0.65,
};

export type ArModelStatus = 'loading' | 'ready' | 'error';

// @google/model-viewer and the three.js exporters are only needed by
// customers who actually reach a 3D/AR render — dynamically imported here
// so the main bundle stays untouched for everyone else. Shared between
// ArPreviewModal (the full 3D/AR view) and the Order sheet's preview,
// rather than duplicating this build-and-cleanup logic in both.
export function useArModelBlobs(active: boolean, imageUrl: string, sizeLabel: string, mapStyleKey: MapStyleKey) {
  const [status, setStatus] = useState<ArModelStatus>('loading');
  const blobsRef = useRef<ArModelBlobs | null>(null);
  const [urls, setUrls] = useState<{ glbUrl: string; usdzUrl: string } | null>(null);

  useEffect(() => {
    if (!active) {
      // Drop any model from a previous activation so a reopen (e.g. the AR
      // modal reopened on a new location, still waiting for its fresh map
      // image) shows the spinner, never the stale print.
      blobsRef.current?.cleanup();
      blobsRef.current = null;
      setUrls(null);
      setStatus('loading');
      return;
    }
    let cancelled = false;
    setStatus('loading');
    setUrls(null);

    (async () => {
      try {
        const [{ buildArModelBlobs }] = await Promise.all([
          import('./arModel'),
          import('@google/model-viewer'),
        ]);
        if (cancelled) return;

        const sizeMeters = SIZE_METERS[sizeLabel] ?? 0.55;
        const blobs = await buildArModelBlobs(imageUrl, sizeMeters, mapStyleKey);
        if (cancelled) {
          blobs.cleanup();
          return;
        }

        blobsRef.current = blobs;
        setUrls({ glbUrl: blobs.glbUrl, usdzUrl: blobs.usdzUrl });
        setStatus('ready');
      } catch (error) {
        console.error('Failed to build AR model:', error);
        if (!cancelled) setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      blobsRef.current?.cleanup();
      blobsRef.current = null;
    };
  }, [active, imageUrl, sizeLabel, mapStyleKey]);

  return { status, urls };
}
